import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { can } from '@seller/shared';
import { api, ApiError, type AnalysisStatus } from '../api/client.js';
import { useSession } from '../auth/useSession.js';
import { Alert, Button } from './ui.js';
import { day } from '../format.js';

/**
 * A shared banner, on the Restock, SKUs, and SKU pages, driven by the derived
 * tables' status. It says when the analysis is updating or out of date, offers
 * an importer a Rebuild button, warns when the inventory ledger is behind the
 * latest census, and — when stock is a forward estimate because the newest
 * census is past the ledger — says what to upload to make it exact.
 *
 * It polls the status endpoint only while a rebuild is running. When a run
 * finishes it invalidates the data queries so the tables refresh on their own.
 */

/** A rebuild running longer than this is treated as possibly stuck: offer Rebuild. */
const STUCK_MS = 15 * 60 * 1000;

export function AnalysisBanner() {
  const session = useSession();
  const canRebuild = session.status === 'signedIn' && can(session.user.role, 'data:import');
  const queryClient = useQueryClient();

  const status = useQuery({
    queryKey: ['analysis-status'],
    queryFn: () => api.get<AnalysisStatus>('/api/analysis/status'),
    refetchInterval: (query) => (query.state.data?.running ? 5000 : false),
  });

  const rebuild = useMutation({
    mutationFn: () => api.post<AnalysisStatus>('/api/analysis/rebuild'),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['analysis-status'] });
    },
  });

  // When a rebuild finishes (running true -> false), refresh the tables it feeds.
  const wasRunning = useRef(false);
  const running = status.data?.running ?? false;
  useEffect(() => {
    if (wasRunning.current && !running) {
      for (const key of ['restock', 'skus', 'sku', 'coverage']) {
        void queryClient.invalidateQueries({ queryKey: [key] });
      }
    }
    wasRunning.current = running;
  }, [running, queryClient]);

  const data = status.data;
  if (!data) return null;

  const startedMs = data.startedAt ? Date.parse(data.startedAt) : null;
  const stuck = running && startedMs !== null && Date.now() - startedMs > STUCK_MS;
  const offerRebuild = data.dirty || stuck;

  const notices = [];

  if (running && !stuck) {
    notices.push(
      <Alert key="running" tone="info">
        Updating analysis…
      </Alert>,
    );
  } else if (offerRebuild) {
    notices.push(
      <Alert key="dirty" tone="warning">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <span>
            {data.ok === false ? 'The last analysis rebuild failed.' : 'Analysis is out of date.'}
            {canRebuild && data.ok === false && data.error ? (
              <span className="block text-xs opacity-80">{data.error}</span>
            ) : null}
          </span>
          {canRebuild && (
            <Button
              variant="secondary"
              disabled={rebuild.isPending}
              onClick={() => rebuild.mutate()}
            >
              {rebuild.isPending ? 'Rebuilding…' : 'Rebuild now'}
            </Button>
          )}
        </div>
        {rebuild.isError && (
          <span className="block text-xs">
            {rebuild.error instanceof ApiError
              ? rebuild.error.message
              : 'Could not start a rebuild.'}
          </span>
        )}
      </Alert>,
    );
  }

  if (data.stockEstimated && data.newestCensus) {
    notices.push(
      <Alert key="estimated" tone="warning">
        Stock history will be exact once an Inventory Ledger covering {day(data.newestCensus)} is
        uploaded. Until then, on-hand and sell-through are estimated and left out.
      </Alert>,
    );
  }

  if (data.ledgerBehind && data.ledgerThrough) {
    notices.push(
      <Alert key="behind" tone="warning">
        Inventory ledger loaded through {day(data.ledgerThrough)}. Stock history after that date is
        unknown; upload a newer ledger.
      </Alert>,
    );
  }

  return notices.length > 0 ? <div className="space-y-2">{notices}</div> : null;
}
